import { SectionSpoiler } from 'app/shared/components/generic-ui/SectionSpoiler';
import { useRouteParams } from 'app/shared/hooks/useRouteParams';
import { Table } from 'app/shared/components/generic-ui/Table/Table';
import { useNotificationEntityApi } from 'app/shared/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useStyles } from './GenericSettings.styles';
import { translations } from 'app/locales/i18n';
import { useTranslation } from 'react-i18next';
import {
  CellAlignment,
  CellSize,
  PaginationType,
} from 'app/shared/components/generic-ui/Table/Table.types';
import { Routes, NotificationEntityType, INotificationEntity } from 'types';
import { Switch } from '@mui/material';
import { CustomTypography } from 'app/shared/components/generic-ui/Typography/Typography';
import { useAuth } from 'app/shared/providers';
import { Button } from 'app/shared/components/generic-ui/Button/Button';
import {
  PopoverSize,
  Select,
  useSelectWithSearch,
} from 'app/shared/components/generic-ui';

const skipOnePage = 1;
const dropdownPerPage = 50;

interface IGenericSettingsProps {
  entityType: NotificationEntityType;
  header: string;
  label: string;
  emptyTableText: string;
}

export const GenericSettings = ({
  entityType,
  header,
  label,
  emptyTableText,
}: IGenericSettingsProps) => {
  const { t } = useTranslation();
  const { isMember } = useAuth();
  const [activeSearch, setActiveSearch] = useState('');
  const [notificationEntitiesCache, setNotificationEntitiesCache] = useState<
    INotificationEntity[]
  >([]);
  const { classes } = useStyles();
  const {
    perPage,
    tab,
    organizationId,
    page,
  }: {
    page: string;
    perPage: string;
    orderType: string;
    organizationId: string;
    tab: string;
  } = useRouteParams();

  const queryParams = useMemo(() => {
    return {
      take: perPage,
      query: activeSearch,
      skip: `${(+page - skipOnePage) * +perPage}`,
      sortBy: 'lastTicketDispatchDateTimeUtc',
      sortAsc: false,
    };
  }, [activeSearch, page, perPage]);

  const {
    loadMoreHandler,
    searchHandler,
    page: searchQueryPage,
    searchQuery,
  } = useSelectWithSearch();

  const searchQueryParams = useMemo(() => {
    return {
      organizationId: organizationId,
      entityType: entityType,
      queryParameters: {
        sortBy: 'lastTicketDispatchDateTimeUtc',
        sortAsc: false,
        query: searchQuery,
        skip: (searchQueryPage - skipOnePage) * dropdownPerPage,
        take: dropdownPerPage,
      },
    };
  }, [entityType, organizationId, searchQuery, searchQueryPage]);

  const {
    fetchNotificationEntities: fetchSearchQueryEntities,
    notificationEntities: searchQueryEntities,
    notificationEntitiesResponse: searchQueryEntitiesResponse,
  } = useNotificationEntityApi(searchQueryParams);

  useEffect(() => {
    if (entityType !== NotificationEntityType.ORGANIZATION)
      fetchSearchQueryEntities();
  }, [entityType, fetchSearchQueryEntities, searchQueryParams]);

  const showPaginationLoader: boolean | undefined = useMemo(() => {
    return searchQueryEntities?.length &&
      searchQueryEntitiesResponse?.metadata?.totalCount
      ? searchQueryEntitiesResponse?.metadata?.totalCount >
          searchQueryEntities?.length
      : false;
  }, [
    searchQueryEntities?.length,
    searchQueryEntitiesResponse?.metadata?.totalCount,
  ]);

  const {
    fetchNotificationEntities,
    isFetchingNotificationEntities,
    notificationEntitiesResponse,
    notificationEntities,
    updateReuseNotificationEntity,
  } = useNotificationEntityApi({
    organizationId: organizationId,
    entityType: entityType,
    // @ts-ignore
    queryParameters: queryParams,
    isInfiniteScrolling: false,
  });

  const applyFilterHandler = useCallback(() => {
    setActiveSearch(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    fetchNotificationEntities();
  }, [fetchNotificationEntities, queryParams]);

  useEffect(() => {
    setNotificationEntitiesCache(notificationEntities);
  }, [notificationEntities]);

  const updateSettings = useCallback(
    async (
      nonReusable: boolean,
      noReturns: boolean,
      entity?: INotificationEntity | INotificationEntity[],
    ) => {
      const payload = Array.isArray(entity)
        ? entity.map(el => ({
            internalId: el?.internalId || '',
            nonReusable,
            noReturns,
          }))
        : [
            {
              internalId: entity?.internalId || '',
              nonReusable,
              noReturns,
            },
          ];

      if (payload.length) {
        updateReuseNotificationEntity(payload);
        setNotificationEntitiesCache(prev =>
          prev.map(el =>
            payload.find(p => p.internalId === el.internalId)
              ? { ...el, nonReusable, noReturns }
              : el,
          ),
        );
      }
    },
    [updateReuseNotificationEntity],
  );

  const updateSettingsAllSettings = useCallback(
    async (
      nonReusable: boolean | undefined,
      noReturns: boolean | undefined,
      entity: INotificationEntity[],
    ) => {
      const payload = entity.map(el => ({
        internalId: el?.internalId || '',
        nonReusable: nonReusable === undefined ? !!el.nonReusable : nonReusable,
        noReturns: noReturns === undefined ? !!el.noReturns : noReturns,
      }));

      if (payload.length) {
        updateReuseNotificationEntity(payload);
        setNotificationEntitiesCache(prev =>
          prev.map(el =>
            payload.find(p => p.internalId === el.internalId)
              ? {
                  ...el,
                  nonReusable:
                    nonReusable === undefined ? !!el.nonReusable : nonReusable,
                  noReturns:
                    noReturns === undefined ? !!el.noReturns : noReturns,
                }
              : el,
          ),
        );
      }
    },
    [updateReuseNotificationEntity],
  );

  const columns = useMemo(() => {
    return [
      {
        id: 'externalId',
        label: 'ID',
        alignment: CellAlignment.LEFT,
        size: CellSize.MEDIUM,
        sortable: false,
      },
      {
        id: 'name',
        label,
        alignment: CellAlignment.LEFT,
        size: CellSize.MEDIUM,
        sortable: false,
      },
      {
        id: 'nonReusable',
        label: t(translations.genericSettings.nonReusable),
        alignment: CellAlignment.RIGHT,
        size: CellSize.MEDIUM_SMALL,
        sortable: false,
      },
      {
        id: 'noReturns',
        label: t(translations.genericSettings.noReturns),
        alignment: CellAlignment.RIGHT,
        size: CellSize.MEDIUM_SMALL,
        sortable: false,
      },
    ];
  }, [label, t]);

  const searchQueryColumns = [
    {
      id: 'externalId',
      label: 'ID',
      alignment: CellAlignment.LEFT,
      size: CellSize.SMALL,
    },
    {
      id: 'name',
      label: 'Name',
      alignment: CellAlignment.LEFT,
      size: CellSize.SMALL,
    },
    {
      id: 'notificationEntityGroupName',
      label: 'Group Name',
      alignment: CellAlignment.LEFT,
      size: CellSize.MEDIUM,
    },
  ];

  const renderCell = useCallback(
    (rowData: any, cellId: string) => {
      if (cellId === 'nonReusable' || cellId === 'noReturns') {
        return (
          <div className={classes.switchContainer}>
            {!!isMember && (
              <CustomTypography
                variant="subtitle1"
                bold
                color={rowData[cellId] ? 'mbsBlue' : 'greyscale2'}
              >
                {rowData[cellId]
                  ? t(translations.genericSettings.on)
                  : t(translations.genericSettings.off)}
              </CustomTypography>
            )}

            <Switch
              disabled={isMember}
              checked={!!rowData[cellId]}
              onChange={() => {
                updateSettings(
                  cellId === 'nonReusable'
                    ? !rowData.nonReusable
                    : !!rowData.nonReusable,
                  cellId === 'noReturns'
                    ? !rowData.noReturns
                    : !!rowData.noReturns,
                  rowData,
                );
              }}
              color="primary"
              className={classes.toggle}
              classes={{
                root: classes.rootSwitch,
                switchBase: classes.switchBase,
                track: classes.switchTrack,
                thumb: classes.thumb,
              }}
              slotProps={{
                input: { 'aria-label': 'controlled' },
              }}
            />
          </div>
        );
      } else {
        return <div>{rowData[cellId] || ''}</div>;
      }
    },
    [
      classes.rootSwitch,
      classes.switchBase,
      classes.switchContainer,
      classes.switchTrack,
      classes.thumb,
      classes.toggle,
      isMember,
      t,
      updateSettings,
    ],
  );

  const searchQueryRenderCell = useCallback((rowData: any, cellId: string) => {
    return <div style={{ whiteSpace: 'nowrap' }}>{rowData[cellId]}</div>;
  }, []);

  return (
    <div>
      <div className={classes.headerContainer}>
        <div className={classes.input}>
          <CustomTypography variant="caption1" color="greyscale1">
            {t(translations.filterBlock.search)}
          </CustomTypography>

          <Select.Search
            className={classes.input}
            searchQuery={searchQuery}
            isLoading={isFetchingNotificationEntities}
            valueId={'name'}
            labelId={'name'}
            options={searchQueryEntities}
            selectedValues={[]}
            onChangeSelectedValue={value => {
              searchHandler(value);
            }}
            showLoader={showPaginationLoader}
            loadMoreHandler={loadMoreHandler}
            withPagination={true}
            onSearchHandler={searchHandler}
            popoverSize={PopoverSize.BIG}
            isMultiSelect={false}
            renderCell={searchQueryRenderCell}
            columns={searchQueryColumns}
          />
        </div>

        <div className={classes.applyButton}>
          <Button
            onClick={applyFilterHandler}
            height={42}
            variant={'secondaryLight'}
          >
            <CustomTypography variant="buttonTextSmall" bold color={'mbsBlue'}>
              {t(translations.datePicker.apply)}
            </CustomTypography>
          </Button>
        </div>
      </div>
      <SectionSpoiler header={header} withIcons={true}>
        {!isMember && (
          <div className={classes.actions}>
            <div
              className={classes.activateAll}
              onClick={() => {
                updateSettings(true, true, notificationEntitiesCache);
              }}
            >
              <CustomTypography variant="buttonTextSmall" bold color="mbsBlue">
                {t(translations.truckListing.activateAll)}
              </CustomTypography>
            </div>
            <div
              className={classes.deactivateAll}
              onClick={() => {
                updateSettings(false, false, notificationEntitiesCache);
              }}
            >
              <CustomTypography variant="buttonTextSmall" bold color="mbsBlue">
                {t(translations.truckListing.deactivateAll)}
              </CustomTypography>
            </div>
            <div
              className={classes.deactivateAll}
              onClick={() => {
                updateSettingsAllSettings(
                  true,
                  undefined,
                  notificationEntitiesCache,
                );
              }}
            >
              <CustomTypography variant="buttonTextSmall" bold color="mbsBlue">
                {t(translations.truckListing.activateAllNonReusable)}
              </CustomTypography>
            </div>
            <div
              className={classes.deactivateAll}
              onClick={() => {
                updateSettingsAllSettings(
                  false,
                  undefined,
                  notificationEntitiesCache,
                );
              }}
            >
              <CustomTypography variant="buttonTextSmall" bold color="mbsBlue">
                {t(translations.truckListing.deactivateAllNonReusable)}
              </CustomTypography>
            </div>
            <div
              className={classes.deactivateAll}
              onClick={() => {
                updateSettingsAllSettings(
                  undefined,
                  true,
                  notificationEntitiesCache,
                );
              }}
            >
              <CustomTypography variant="buttonTextSmall" bold color="mbsBlue">
                {t(translations.truckListing.activateAllNoReturns)}
              </CustomTypography>
            </div>
            <div
              className={classes.deactivateAll}
              onClick={() => {
                updateSettingsAllSettings(
                  undefined,
                  false,
                  notificationEntitiesCache,
                );
              }}
            >
              <CustomTypography variant="buttonTextSmall" bold color="mbsBlue">
                {t(translations.truckListing.deactivateAllNoReturns)}
              </CustomTypography>
            </div>
          </div>
        )}
        <Table
          withHover={true}
          withPerPageCount={true}
          withPagination={true}
          columns={columns}
          tableData={notificationEntitiesCache}
          withCheckbox={false}
          isLoading={isFetchingNotificationEntities}
          emptyTableText={`${emptyTableText}.`}
          baseURL={Routes.OrganizationDetails.replace(
            ':organizationId/:tab',
            `${organizationId}/${tab}`,
          )}
          paginationType={PaginationType.MAIN}
          rowsTotal={notificationEntitiesResponse?.metadata?.totalCount}
          renderCell={renderCell}
          withPadding={false}
        />
      </SectionSpoiler>
    </div>
  );
};
